import {Context} from "../../index";
import React, {useContext, useEffect, useRef, useState} from "react";
import {Form, Button, Spinner, Table, Card, Pagination} from "react-bootstrap";
import { toast } from "react-toastify";
import {
    requestTransferVirtToAnotherAccountApi,
    confirmCodeToTransferVirtApi,
    fetchTransferVirtToAnotherAccountApi, check
} from "../../http/userAPI";
import CacheUpdateModal from "../../helpers/CacheUpdateModal"; // обнови путь, если отличается

const TransferVirtToFriend = ({ refreshKey = 0 }) => {
        const { user } = useContext(Context);
    const REVIEWS_QUALITY_MIN = 60;

    // --- Transfer guard based on reviewerData ---
    const rq = Number(user?.reviewerData?.reviewQuality100 ?? NaN);
    const complaints30 = Number(user?.reviewerData?.reviewerComplaintCount30Day ?? 0);
    const trust = Number(user?.user?.level_of_trust ?? user?.reviewerData?.level_of_trust ?? NaN);

    const isRqBad = Number.isFinite(rq) && rq < REVIEWS_QUALITY_MIN;
    const isComplaintsBad = complaints30 >= 3;
    const isTrustBad = !Number.isFinite(trust) || trust < 200;

    const transferBlocked = isRqBad || isComplaintsBad || isTrustBad;

    const transferBlockReason = (() => {
        const reasons = [];
        if (isRqBad) reasons.push(`Reviews Quality ${Number.isFinite(rq) ? rq : "—"}% < ${REVIEWS_QUALITY_MIN}`);
        if (isComplaintsBad) reasons.push(`Complaints (30d) ${complaints30} ≥ 3`);
        if (isTrustBad) reasons.push(`Trust Level ${Number.isFinite(trust) ? trust : "—"} < 200`);
        return reasons.length ? `Transfers are blocked: ${reasons.join(" and ")}.` : null;
    })();

    const emailValidationTimeout = useRef(null);
        const [errors, setErrors] = useState({});
    const [openRows, setOpenRows] = useState(new Set());



    const [formData, setFormData] = useState({
            toEmail: "",
            amount: "",
            message: ""
        });

        const [codeInputs, setCodeInputs] = useState({});
        const [loading, setLoading] = useState(false);
        const [transfers, setTransfers] = useState([]);
        const [transfersCount, setTransfersCount] = useState(0);
        const [transfersPage, setTransfersPage] = useState(1);
        const [transfersLimit] = useState(25);
        const [loadingTransfers, setLoadingTransfers] = useState(true);
    const [availableToTransfer, setAvailableToTransfer] = useState(0);
    const [breakdown, setBreakdown] = useState(null); // { balance, pendingVirts, minReserve, formula }
    const transfersTotalPages = Math.max(1, Math.ceil(transfersCount / transfersLimit));
    const secondaryActivePageStyle = {
        backgroundColor: "#6c757d",
        borderColor: "#6c757d",
        color: "#fff",
    };


    const toNum = (v) => (v == null || v === '' ? 0 : Number(v));

    // toggle раскрытия строки
    const toggleRow = (id) =>
        setOpenRows((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

// удобный флаг
    const isOpen = (id) => openRows.has(id);

    const fetchTransfers = async () => {
        try {
            setLoadingTransfers(true);
            const data = await fetchTransferVirtToAnotherAccountApi(user.user.id, {
                limit: transfersLimit,
                offset: (transfersPage - 1) * transfersLimit,
            });

            // список переводов (как и было)
            const transferRows = data?.transfers || [];
            user.setOrderstotransfervirt(transferRows);
            setTransfers([...transferRows]);
            setTransfersCount(Number(data?.transfersCount ?? transferRows.length ?? 0));
            setOpenRows(new Set());

            // новые поля от бэка
            const b = data?.breakdown || {};
            const balance = toNum(b.balance);
            const pendingVirts = toNum(b.pendingVirts);
            const minReserve = toNum(b.minReserve ?? 1500);

            // что пришло с сервера
            let avail = toNum(data?.availableTransferVirt);

            // на всякий случай, если сервер не прислал — посчитаем из breakdown
            if (!data?.availableTransferVirt && data?.breakdown) {
                avail = Math.max(balance - pendingVirts - minReserve, 0);
            }

            setAvailableToTransfer(Math.max(avail, 0));
            setBreakdown({ balance, pendingVirts, minReserve, formula: b.formula });
        } catch (e) {
            toast.error("Failed to load transfers", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setLoadingTransfers(false);
        }
    };

    useEffect(() => {
        if (user?.user?.id) fetchTransfers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.user.id, refreshKey, transfersPage]);

    const handleEmailChange = (e) => {
        const raw = e.target.value;                // храним как есть (с пробелами, если пользователь их ввёл)
        setFormData(prev => ({ ...prev, toEmail: raw }));

        if (emailValidationTimeout.current) clearTimeout(emailValidationTimeout.current);

        emailValidationTimeout.current = setTimeout(() => {
            const candidate = raw.trim().toLowerCase();             // нормализуем ТОЛЬКО для проверки
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            setErrors(prev => ({
                ...prev,
                toEmail: emailRegex.test(candidate) ? null : "Invalid email format",
            }));
        }, 3000);
    };

    const handleMessageChange = (e) => {
        setFormData(prev => ({ ...prev, message: e.target.value }));  // никаких trim
    };

    const handleAmountChange = (e) => {
        setFormData(prev => ({ ...prev, amount: e.target.value }));
    };

        const handleRequestSubmit = async (e) => {
            e.preventDefault();
            if (transferBlocked) {
                toast.warning(transferBlockReason || "Transfers are currently unavailable for your account.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
                return;
            }
            setLoading(true);
            try {
                const response = await requestTransferVirtToAnotherAccountApi({
                    userId: user.user.id,
                    toEmail: formData.toEmail.trim().toLowerCase(),
                    amount: parseFloat(formData.amount),
                    message: formData.message.trim()
                });
                toast.success(response.message || "Request submitted", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
                setFormData({ toEmail: "", amount: "", message: "" });
                fetchTransfers();
            } catch (e) {
                toast.error(e.response?.data?.message || "Transfer request failed", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            } finally {
                setLoading(false);
            }
        };

        const handleCodeChange = (id, val) => {
            const trimmed = val.replace(/\D/g, "").slice(0, 6);
            setCodeInputs((prev) => ({ ...prev, [id]: trimmed }));
        };

        const handleConfirmCode = async (id) => {
            try {
                const code = codeInputs[id];
                if (!/^\d{6}$/.test(code)) {
                    toast.warning("Please enter a valid 6-digit code.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
                    return;
                }
                const res = await confirmCodeToTransferVirtApi({ orderId: id, confirmCode: code });
                toast.success(res.message || "Transfer completed!", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
                fetchTransfers();
                const data = await check();
                user.setUser(data);
            } catch (e) {
                toast.error(e.response?.data?.message || "Code confirmation failed", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            }
        };

    const maxAmount = Math.max(Number(availableToTransfer ?? 0), 0);
    const overLimit = Number(formData.amount || 0) > maxAmount;

        return (
            <>
                <Card className="p-3 w-100 mt-0">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
                        <h5
                            className="text-muted mb-0 mb-md-0"
                            title={
                                transferBlocked
                                    ? `${transferBlockReason} Transfers are temporarily unavailable for your account.`
                                    : "Transfer Virt to another user"
                            }
                        >
                            Transfer Virt to Another User
                            {transferBlocked && (
                                <span className="ms-2 badge bg-danger" role="alert">
      blocked
    </span>
                            )}
                        </h5>
                        {transferBlocked && (
                            <div className="alert alert-warning py-2 px-3 my-2 small" role="alert">
                                {transferBlockReason} Improve your review quality and reduce refund incidents to enable transfers again.
                            </div>
                        )}

                <small className="text-muted d-block">
                    To transfer Virt from this account to another, enter the recipient's email, the amount, and an optional comment. Then click "Request Transfer" — you will receive an email with a confirmation code. Enter the code to complete the transfer. Each request is valid for 10 minutes. A non-withdrawable minimum balance of 1500 Virt is required.
                    <div className="text-muted d-block mt-2">
                    <strong className="text-danger mt-2">Important:</strong> Due to unreliable actions from some irresponsible users, Virt transfers are only available for Confirmed Assignments.
                    Pending Virt — meaning funds already credited to your balance but linked to assignments that are still in progress — cannot be transferred.
                    </div>

                    <div className="text-muted d-block mt-2 mb-1">
                        <strong className="text-danger me-1">Available amount for transfer:</strong>
                        <strong className="me-2">{availableToTransfer} Virt</strong>
                        {breakdown && (
                            <span>
                                (<span className="fst-italic">Calculated as:</span> Balance ({Number(breakdown.balance)} Virt) − Pending Virt ({Number(breakdown.pendingVirts)} Virt) − Min Reserve ({Number(breakdown.minReserve)} Virt) = <strong>{Number(breakdown.balance) - Number(breakdown.pendingVirts) - Number(breakdown.minReserve)} Virt</strong>)
    </span>
                        )}
                    </div>
                    <div className="text-muted d-block mt-1 mb-3">
                        <strong>Reviews Quality:</strong>{" "}
                        {Number.isFinite(rq) ? `${rq}%` : "—"}{" "}
                        <span className={isRqBad ? "text-danger fw-bolder" : "text-success fw-semibold"}>
    {isRqBad
        ? `(You need at least ${REVIEWS_QUALITY_MIN}% positive reviews)`
        : "(OK)"}
  </span>
                        <br />
                        <strong>Complaints in last 30 days:</strong>{" "}
                        {complaints30}{" "}
                        <span className={isComplaintsBad ? "text-danger fw-bolder" : "text-success fw-semibold"}>
    {isComplaintsBad
        ? "(You can have no more than 2 complaints in 30 days)"
        : "(OK)"}
  </span>
                        <br />
                        <strong>Trust Level:</strong>{" "}
                        {Number.isFinite(trust) ? trust : "—"}{" points "}
                        <span className={isTrustBad ? "text-danger fw-bolder" : "text-success fw-semibold"}>
  {isTrustBad ? "(You need > 200 points to transfer)" : "(OK)"}
</span>
                        <br />
                        <span>
  <em>
    To transfer Virt, your Reviews Quality must be {REVIEWS_QUALITY_MIN}% or more, you must have fewer than 3 complaints
    in the last 30 days, and your Trust must be above 200.
  </em>
</span>

                    </div>

                </small>
                <Form onSubmit={handleRequestSubmit} className="d-flex gap-3 flex-wrap align-items-end w-100">
                    <Form.Group className="position-relative">

                        <Form.Label className="small text-muted mb-1">Recipient Email</Form.Label>
                        <Form.Control
                            size="sm"
                            className="form-control-sm text-success"
                            style={{ width: "180px" }}
                            disabled={transferBlocked}
                            name="toEmail"
                            value={formData.toEmail}
                            onChange={handleEmailChange}         // <-- тут
                            placeholder="Enter E-Mail to transfer"
                            required
                            isInvalid={!!errors.toEmail}
                        />
                        <Form.Control.Feedback type="invalid" tooltip>
                            {errors.toEmail}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group controlId="transferAmount" className="position-relative">
                        <Form.Label className="small text-muted mb-1">Sum to Transfer: </Form.Label>
                        <Form.Control
                            size="sm"
                            type="number"
                            min={10}
                            style={{ width: "180px" }}
                            disabled={transferBlocked}
                            max={maxAmount}
                            step="10"
                            name="amount"
                            value={formData.amount}
                            onChange={handleAmountChange}        // <-- тут
                            placeholder="Enter SUM"
                            required
                            isInvalid={overLimit}
                        />
                        <Form.Control.Feedback type="invalid" tooltip>
                            Maximum is {maxAmount} Virt.
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="flex-grow-1">
                        <Form.Label className="small text-muted mb-1">Message (optional)</Form.Label>
                        <Form.Control
                            as="textarea"                        // <-- textarea, чтобы сохранить переносы
                            rows={1}
                            size="sm"
                            name="message"
                            disabled={transferBlocked}
                            value={formData.message}
                            onChange={handleMessageChange}       // <-- тут
                            placeholder="Enter message for recipient"
                        />
                    </Form.Group>

                    <Button
                        type="submit"
                        size="sm"
                        className="btn-success"
                        disabled={
                            loading ||
                            transferBlocked ||
                            Number(formData.amount || 0) <= 0 ||
                            Number(formData.amount || 0) > Number(availableToTransfer ?? 0)
                        }
                    >
                        {loading ? <Spinner animation="border" size="sm" /> : "Send Request"}
                    </Button>
                </Form>



                {loadingTransfers ? (
                    <div className="text-muted">Loading transfers...</div>
                ) : transfers.length === 0 ? (
                    <div className="text-muted">No transfer records yet.</div>
                ) : (

                    <div className="w-100" style={{ overflowX: 'auto', whiteSpace: 'nowrap', paddingBottom: '8px' }}>
                        <hr />
                        <strong className="text-danger mt-2 small">Your Transfers:</strong>
                        <Table bordered hover size="sm" striped className="mt-1" style={{ minWidth: '600px', lineHeight: '1.2' }}>
                            <thead className="table-light text-muted small">
                            <tr className="text-muted small">
                                <th className="text-muted text-center">ID</th>
                                <th className="text-muted">Date</th>
                                <th className="text-muted">To Email</th>
                                <th className="text-muted text-center">Recipient</th>
                                <th className="text-muted text-center">Amount</th>
                                <th className="text-muted text-center">Status</th>
                                <th className="text-muted text-center">Code</th>
                                <th className="text-muted text-center">Action</th>
                                <th className="text-muted text-center">Message</th>
                            </tr>
                            </thead>
                            <tbody>
                            {transfers.map((t) => (
                                <React.Fragment key={t.id}>
                                    {/* ОСНОВНАЯ СТРОКА */}
                                    <tr
                                        onClick={() => toggleRow(t.id)}
                                        style={{ cursor: "pointer" }}
                                        className={isOpen(t.id) ? "table-active small fst-italic" : "small fst-italic"}
                                    >
                                        <td className="text-center small align-middle" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>

                                        {isOpen(t.id) ? "▾ " : "▸ "}
                                            {t.id}
                                        </td>
                                        <td className="text-start small align-middle" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>

                                        {new Date(t.transfer_date).toLocaleString("en-GB", {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                hour12: false,
                                            }).replace(",", "")}
                                        </td>
                                        <td className="text-start small align-middle" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {t.to_email}</td>
                                        <td className="text-center small align-middle" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {t.recipient_name}</td>
                                        <td className="text-center small text-success fw-bold align-middle" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {t.transfer_amount} Virt</td>
                                        <td className="text-center align-middle " style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>

     <span
         className={`text-uppercase small fw-bold fst-normal ${
             t.transfer_status === "completed"
                 ? "text-success"
                 : t.transfer_status === "pending"
                     ? "text-warning"
                     : t.transfer_status === "failed"
                         ? "text-danger"
                         : "text-secondary"
         }`}
     >
  {String(t.transfer_status)}
</span>
                                        </td>

                                        {/* Code input — НЕ раскрывает строку */}
                                        <td onClick={(e) => e.stopPropagation()}>
                                            {t.transfer_status === "pending" ? (
                                                <div className="d-flex justify-content-center">
                                                    <Form.Control
                                                        size="sm"
                                                        type="text"
                                                        maxLength={6}
                                                        placeholder="Enter 6-digit"
                                                        style={{ width: "120px" }}
                                                        value={codeInputs[t.id] || ""}
                                                        onChange={(e) => handleCodeChange(t.id, e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                            ) : (
                                                "-"
                                            )}
                                        </td>

                                        {/* Confirm — НЕ раскрывает строку */}
                                        <td onClick={(e) => e.stopPropagation()}>
                                            {t.transfer_status === "pending" ? (
                                                <Button
                                                    size="sm"
                                                    variant="outline-success"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleConfirmCode(t.id);
                                                    }}
                                                >
                                                    Confirm
                                                </Button>
                                            ) : (
                                                "-"
                                            )}
                                        </td>

                                        {/* «Message» колонка — вместо текста даём подсказку кликнуть */}
                                        <td className="text-muted text-center small align-middle">
                                            {isOpen(t.id) ? "Click to hide" : "Click to view"}
                                        </td>
                                    </tr>

                                    {/* ДЕТАЛЬНАЯ СТРОКА (РАСКРЫТАЯ) */}
                                    {isOpen(t.id) && (
                                        <tr className="small">
                                            <td colSpan={9} className="text-start py-2">
                                                <div className="p-2 rounded-2 border border-1 border-secondary-subtle bg-light-subtle">
                                                    <div className="text-muted fw-semibold mb-1">Comment:</div>
                                                    <div className="text-muted">{t.transfer_comment || "—"}</div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                            </tbody>
                        </Table>

                        {transfersCount > transfersLimit && (
                            <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-2 mt-2">
                                <div className="small text-muted">
                                    Showing {(transfersPage - 1) * transfersLimit + 1}-{Math.min(transfersPage * transfersLimit, transfersCount)} of {transfersCount} transfers
                                </div>
                                <Pagination size="sm" className="mb-0">
                                    <Pagination.First
                                        disabled={transfersPage <= 1 || loadingTransfers}
                                        onClick={() => setTransfersPage(1)}
                                    />
                                    <Pagination.Prev
                                        disabled={transfersPage <= 1 || loadingTransfers}
                                        onClick={() => setTransfersPage((prev) => Math.max(1, prev - 1))}
                                    />
                                    <Pagination.Item active linkStyle={secondaryActivePageStyle}>
                                        {transfersPage} / {transfersTotalPages}
                                    </Pagination.Item>
                                    <Pagination.Next
                                        disabled={transfersPage >= transfersTotalPages || loadingTransfers}
                                        onClick={() => setTransfersPage((prev) => Math.min(transfersTotalPages, prev + 1))}
                                    />
                                    <Pagination.Last
                                        disabled={transfersPage >= transfersTotalPages || loadingTransfers}
                                        onClick={() => setTransfersPage(transfersTotalPages)}
                                    />
                                </Pagination>
                            </div>
                        )}
                    </div>

                )}
            </div>
        </Card>
                </>
        );
    };

export default TransferVirtToFriend;
